import { makeStyles, tokens, Text, Button } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { PrimaryButton } from '../../shared/StyledCommonComponents';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    inputField: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        fontSize: tokens.fontSizeBase300,
        fontFamily: tokens.fontFamilyBase,
        color: tokens.colorNeutralForeground1,
        backgroundColor: tokens.colorNeutralBackground1,
        width: '100%',
        outline: 'none',
        transition: 'border-color 0.2s ease-in-out',
        '&:focus': {
            borderTopColor: tokens.colorBrandStroke1,
            borderRightColor: tokens.colorBrandStroke1,
            borderBottomColor: tokens.colorBrandStroke1,
            borderLeftColor: tokens.colorBrandStroke1,
        },
        '&:hover': {
            borderTopColor: tokens.colorNeutralStroke1Hover,
            borderRightColor: tokens.colorNeutralStroke1Hover,
            borderBottomColor: tokens.colorNeutralStroke1Hover,
            borderLeftColor: tokens.colorNeutralStroke1Hover,
        },
        '&::placeholder': {
            color: tokens.colorNeutralForeground4,
        },
        '&:disabled': {
            backgroundColor: tokens.colorNeutralBackgroundDisabled,
            color: tokens.colorNeutralForegroundDisabled,
            cursor: 'not-allowed',
        },
    },
    buttonContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        justifyContent: 'center',
        marginTop: tokens.spacingVerticalM,
    },
    errorMessage: {
        display: 'block',
        color: tokens.colorPaletteRedForeground1,
        fontSize: tokens.fontSizeBase200,
        textAlign: 'center',
        backgroundColor: tokens.colorPaletteRedBackground1,
        borderRadius: tokens.borderRadiusMedium,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        marginBottom: tokens.spacingVerticalS,
    },
});

interface CallbackPhoneInputProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    onBack: () => void;
    disabled?: boolean;
    errorMessage?: string | null;
}

export const CallbackPhoneInput: React.FC<CallbackPhoneInputProps> = ({
    value,
    onChange,
    onSubmit,
    onBack,
    disabled = false,
    errorMessage,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit();
    };

    return (
        <form onSubmit={handleSubmit} className={classes.container}>
            {errorMessage && <Text className={classes.errorMessage}>{errorMessage}</Text>}
            <input
                type="tel"
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                }}
                placeholder={t('callback.phoneNumberPlaceholder')}
                className={classes.inputField}
                required
                disabled={disabled}
            />
            <div className={classes.buttonContainer}>
                <Button appearance="secondary" onClick={onBack} disabled={disabled}>
                    {t('callback.backButton')}
                </Button>
                <PrimaryButton type="submit" disabled={!value.trim() || disabled}>
                    {t('callback.callMeButton')}
                </PrimaryButton>
            </div>
        </form>
    );
};
